import React, { useEffect, useRef, useState } from 'react';
import type { PassportCard as Card } from '../../services/api/passportCards';
import CtaIcon from '../ui/CtaIcon';
import PartnerRevealEffects, {
  previewRevealProfiles,
  type PreviewKind,
} from '../partners/PartnerRevealEffects';
import PassportCard from './PassportCard';
import './passport-reveal-showcase.css';

export type PassportRevealSample = {
  id: PreviewKind;
  label: string;
  title: string;
  detail: string;
  action: string;
  resultDetail: string;
  backgroundImage: string;
  card: Card;
};

type Props = {
  id?: string;
  eyebrow: string;
  heading: React.ReactNode;
  intro: string;
  samples: PassportRevealSample[];
  disclosure: React.ReactNode;
  tone?: 'bilt' | 'flat-six';
  className?: string;
  cueSelector?: boolean;
};

/**
 * Shared, non-persistent preview of the production collector reveal grammar.
 * The outer stage owns arrival motion; PassportCard exclusively owns the hinge.
 */
export default function PassportRevealShowcase({
  id,
  eyebrow,
  heading,
  intro,
  samples,
  disclosure,
  tone = 'bilt',
  className = '',
  cueSelector = false,
}: Props) {
  const [selectedId, setSelectedId] = useState(samples[0]?.id);
  const [replay, setReplay] = useState(0);
  const [paused, setPaused] = useState(false);
  const [back, setBack] = useState(false);
  const [inView, setInView] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  const figure = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      setHasEntered(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setHasEntered(true);
      },
      { threshold: 0.2 }
    );
    const current = figure.current;
    if (current) observer.observe(current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (samples.some((sample) => sample.id === selectedId)) return;
    setSelectedId(samples[0]?.id);
  }, [samples, selectedId]);

  const sample = samples.find((item) => item.id === selectedId) || samples[0];
  if (!sample) return null;
  const running = inView && !paused && !back;

  const choose = (next: PassportRevealSample) => {
    setSelectedId(next.id);
    setBack(false);
    setPaused(false);
    setReplay((value) => value + 1);
  };

  return (
    <section
      id={id}
      className={`pr-showcase pr-showcase-${tone} ${className}`.trim()}
      data-sc-act="flow"
      data-sc-device="stage"
      aria-labelledby={`${id || 'passport-reveal'}-title`}
    >
      <div className="pr-showcase-copy">
        <p className="pr-showcase-eyebrow">{eyebrow}</p>
        <h2 id={`${id || 'passport-reveal'}-title`}>{heading}</h2>
        <p className="pr-showcase-intro">{intro}</p>
        <div
          className="pr-showcase-choices"
          role="group"
          aria-label="Choose a Passport collector card example"
          {...(cueSelector ? { 'data-partner-cue': 'preview-selection' } : {})}
        >
          {samples.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={sample.id === item.id}
              onClick={() => choose(item)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="pr-showcase-explanation" role="status" aria-live="polite">
          <h3>{sample.title}</h3>
          <p>{sample.detail}</p>
        </div>
        <div className="pr-showcase-motion-controls">
          <button
            className="pr-showcase-text-button"
            type="button"
            onClick={() => {
              setPaused(false);
              setReplay((value) => value + 1);
            }}
          >
            Replay the reveal <CtaIcon direction="turn" />
          </button>
          <button
            className="pr-showcase-text-button pr-showcase-motion-toggle"
            type="button"
            onClick={() => setPaused((value) => !value)}
          >
            {paused ? 'Resume animation' : 'Pause animation'}
          </button>
        </div>
        <div className="pr-showcase-disclosure">{disclosure}</div>
      </div>

      <figure className="pr-showcase-figure" ref={figure}>
        <div className="pr-phone">
          <div className="pr-phone-screen">
            <div className="pr-phone-top" aria-hidden="true">
              <span>9:41</span>
              <span className="pr-phone-signal"><i /><i /><i /></span>
            </div>
            <div className="pr-phone-nav" aria-hidden="true">
              <CtaIcon direction="back" compact />
              <strong>Bilt:Spec</strong>
              <span>Scan</span>
            </div>
            <img
              className="pr-phone-camera"
              src={sample.backgroundImage}
              alt=""
              width="1400"
              height="1000"
              loading="lazy"
            />
            <div
              className="pr-phone-reveal"
              key={`${sample.id}-${replay}`}
              data-running={running}
              data-entered={hasEntered}
              data-paused={paused}
              data-side={back ? 'back' : 'front'}
              style={
                {
                  '--reveal-color': previewRevealProfiles[sample.id].colors[0],
                  '--reveal-sheen-cycle': `${previewRevealProfiles[sample.id].sheen}ms`,
                } as React.CSSProperties
              }
            >
              <PartnerRevealEffects kind={sample.id} active={running} />
              <span className="pr-phone-eyebrow">QR recorded</span>
              <div className="pr-phone-card">
                <PassportCard
                  card={sample.card}
                  demo
                  back={back}
                  readableStory
                  backLabel={`${sample.card.title} sample card record`}
                />
                <span className="pr-card-sheen" aria-hidden="true" />
              </div>
              <strong className="pr-phone-added">Sample card reveal</strong>
              <span className="pr-phone-detail">{sample.resultDetail}</span>
              <button
                className="pr-phone-flip"
                type="button"
                aria-pressed={back}
                onClick={() => setBack((value) => !value)}
              >
                <CtaIcon direction="turn" />
                {back ? `Show front of ${sample.card.title}` : `Show record for ${sample.card.title}`}
              </button>
              <div className="pr-phone-actions" aria-label="Illustrative next actions">
                <span>{sample.action}</span>
                <span>View Passport</span>
                <span>Done</span>
              </div>
            </div>
            <span className="pr-phone-home" aria-hidden="true" />
          </div>
        </div>
        <figcaption>Production card treatment · {sample.label.toLowerCase()} sample</figcaption>
      </figure>
    </section>
  );
}
